import {
  ArrowRightIcon,
  FolderIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { api } from "../lib/api-client";
import { adminHref } from "../lib/platform-admin";
import { useAsyncData } from "../lib/use-async-data";
import { useDocumentTitle } from "../lib/use-document-title";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  PageHeader,
  SkeletonList,
} from "../components/ui";

export function PlatformAdminHome() {
  useDocumentTitle("プラットフォーム管理");
  const { data, error } = useAsyncData(
    () =>
      Promise.all([api.listProjects(), api.listUsers()]).then(
        ([projects, users]) => ({ projects: projects.items, users: users.users }),
      ),
    [],
  );

  return (
    <section class="space-y-8">
      <PageHeader
        title="プラットフォーム管理"
        description="Cloudflare Access で保護された全体管理コンソール"
        actions={
          <Button
            as="a"
            href={adminHref("/users")}
            variant="primary"
            iconLeft={<UsersIcon class="h-4 w-4" />}
          >
            ユーザー管理
          </Button>
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <div class="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardBody class="space-y-1">
            <div class="flex items-center gap-2 text-xs font-medium text-fg-subtle">
              <UsersIcon class="h-4 w-4" /> ユーザー
            </div>
            <div class="text-2xl font-semibold text-fg">
              {data ? data.users.length : "—"}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody class="space-y-1">
            <div class="flex items-center gap-2 text-xs font-medium text-fg-subtle">
              <FolderIcon class="h-4 w-4" /> プロジェクト
            </div>
            <div class="text-2xl font-semibold text-fg">
              {data ? data.projects.length : "—"}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody class="space-y-1">
            <div class="flex items-center gap-2 text-xs font-medium text-fg-subtle">
              <UserGroupIcon class="h-4 w-4" /> メンバーシップ
            </div>
            <div class="text-2xl font-semibold text-fg">
              {data
                ? data.projects.reduce((sum, project) => sum + project.members.length, 0)
                : "—"}
            </div>
          </CardBody>
        </Card>
      </div>

      {data === null ? (
        <SkeletonList count={4} rowClass="h-24" />
      ) : data.projects.length === 0 ? (
        <EmptyState
          icon={<FolderIcon class="h-5 w-5" />}
          title="プロジェクトはまだありません"
          description="ユーザー管理画面の「プロジェクトを作成」から追加できます。"
        />
      ) : (
        <ul class="grid gap-3 md:grid-cols-2">
          {data.projects.map((project) => (
            <li key={project.id}>
              <a href={adminHref(`/projects/${project.id}`)} class="group block h-full">
                <Card class="h-full transition group-hover:border-border-strong group-hover:shadow-sm">
                  <CardBody class="flex h-full items-center justify-between gap-4">
                    <div class="min-w-0 space-y-2">
                      <div>
                        <div class="truncate text-sm font-semibold text-fg">
                          {project.name}
                        </div>
                        <div class="truncate font-mono text-xs text-fg-subtle">
                          /{project.slug}
                        </div>
                      </div>
                      <div class="flex flex-wrap gap-1.5">
                        <Badge tone="neutral">
                          {project.members.length} members
                        </Badge>
                        <Badge
                          tone={project.deployHookConfigured ? "success" : "neutral"}
                        >
                          deploy hook {project.deployHookConfigured ? "設定済" : "未設定"}
                        </Badge>
                      </div>
                    </div>
                    <ArrowRightIcon class="h-4 w-4 flex-none text-fg-faint transition group-hover:translate-x-0.5 group-hover:text-fg" />
                  </CardBody>
                </Card>
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
